import { cloneElement, isValidElement, useId, useState, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode } from 'react'
import { cx } from './cx'

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-line-strong bg-bg-subtle px-1 font-sans text-label font-medium tracking-normal text-ink-2">
      {children}
    </kbd>
  )
}

type TooltipProps = {
  content: ReactNode
  side?: 'top' | 'bottom'
  /** A single focusable element; it gets aria-describedby pointing at the tooltip. */
  children: ReactElement<{
    'aria-describedby'?: string
    onKeyDown?: (e: KeyboardEvent) => void
    onBlur?: (e: FocusEvent) => void
    onPointerLeave?: (e: PointerEvent) => void
  }>
}

/**
 * Supplementary hint on hover and keyboard focus, CSS only. Esc hides it (WCAG 1.4.13). Never put
 * essential information here — touch users will not see it. Icon-only buttons have this built in.
 */
export function Tooltip({ content, side = 'top', children }: TooltipProps) {
  const id = useId()
  const [dismissed, setDismissed] = useState(false)
  const child = isValidElement(children)
    ? cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], id].filter(Boolean).join(' '),
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Escape') setDismissed(true)
          children.props.onKeyDown?.(e)
        },
        onBlur: (e: FocusEvent) => {
          setDismissed(false)
          children.props.onBlur?.(e)
        },
        onPointerLeave: (e: PointerEvent) => {
          setDismissed(false)
          children.props.onPointerLeave?.(e)
        },
      })
    : children
  return (
    <span className="group/tip relative inline-flex" data-dismissed={dismissed || undefined}>
      {child}
      <span
        id={id}
        role="tooltip"
        className={cx(
          'pointer-events-none absolute left-1/2 z-30 w-max max-w-64 -translate-x-1/2 rounded-[6px] bg-ink px-2 py-1 text-label font-medium tracking-normal text-ink-inverse opacity-0 transition-opacity duration-150',
          side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          'group-hover/tip:opacity-100 group-hover/tip:delay-300 group-focus-within/tip:opacity-100 group-data-[dismissed]/tip:opacity-0!',
        )}
      >
        {content}
      </span>
    </span>
  )
}
